import React from 'react';

import { createStyles, Theme, withStyles, WithStyles } from '@material-ui/core';
import ClickAwayListener, { ClickAwayListenerProps } from '@material-ui/core/ClickAwayListener';
import Fade from '@material-ui/core/Fade';
import Paper, { PaperProps as MuiPaperProps } from '@material-ui/core/Paper';
import MuiPopper, { PopperProps as MuiPopperProps } from '@material-ui/core/Popper';
import classnames from 'classnames';

const styles = ({ spacing: { unit }, zIndex: { tooltip } }: Theme) =>
  createStyles({
    root: {
      zIndex: tooltip,
      width: 300,
      height: 348,
    },
    variantFullscreen: {
      width: '100% !important',
      height: '100% !important',
    },
    paper: {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      height: '100%',
    },
    gutterBottom: {
      top: `${-1 * unit}px !important`,
    },
  });

export interface PopperProps extends Omit<MuiPopperProps, 'open'>, WithStyles<typeof styles> {
  variant?: 'default' | 'fullscreen';
  onClickAway?: () => void | ClickAwayListenerProps['onClickAway'];
  PaperProps?: MuiPaperProps;
  gutterBottom?: boolean;
}

const Popper: React.FC<PopperProps> = ({
  classes,
  children,
  className,
  variant = 'default',
  onClickAway,
  anchorEl,
  PaperProps,
  gutterBottom,
  ...rest
}) => (
  <MuiPopper
    className={classnames(classes.root, className, {
      [classes.gutterBottom]: gutterBottom,
      [classes.variantFullscreen]: variant === 'fullscreen',
    })}
    anchorEl={anchorEl}
    open={variant === 'fullscreen' || Boolean(anchorEl)}
    modifiers={{
      flip: {
        enabled: true,
      },
      preventOverflow: {
        enabled: true,
        boundariesElement: 'scrollParent',
        // Prevent transform minimum value of 5px
        // https://github.com/mui/material-ui/issues/16477
        padding: 0,
      },
    }}
    transition
    onClick={(event) => {
      // Skip passing the click event to the parent behind the popper
      // https://app.clickup.com/t/h6xxw8
      // Don't prevent default since that might affect components inside the popper
      // https://app.clickup.com/t/2v4u6y
      event.stopPropagation();
    }}
    {...rest}
  >
    {({ TransitionProps }) =>
      onClickAway ? (
        <ClickAwayListener onClickAway={onClickAway}>
          <Fade timeout={100} {...TransitionProps}>
            <Paper className={classes.paper} {...PaperProps}>
              {children}
            </Paper>
          </Fade>
        </ClickAwayListener>
      ) : (
        <Fade timeout={100} {...TransitionProps}>
          <Paper className={classes.paper} {...PaperProps}>
            {children}
          </Paper>
        </Fade>
      )
    }
  </MuiPopper>
);

export default withStyles(styles)(Popper);
